import { useCallback, useEffect, useMemo, useState } from "react"
import { Link, router } from "@inertiajs/react"
import { toast } from "sonner"
import { BookOpen, ChevronDown, Eye, FlaskConical, RefreshCw } from "lucide-react"
import { AppShell } from "@/components/layout/app-shell"
import { VersionSelector } from "@/components/domain/version-selector"
import { VersionTimeline } from "@/components/domain/version-timeline"
import { TestLabTabs } from "@/components/domain/test-lab-tabs"
import { ScenarioList } from "@/components/domain/scenario-list"
import { ScenarioDetail } from "@/components/domain/scenario-detail"
import { DesignerPane } from "@/components/domain/designer-pane"
import { ActivityFeedButton } from "@/components/domain/activity-feed-button"
import { TestPane } from "@/components/domain/test-pane"
import { Dialog, DialogBody, DialogFooter, DialogHeader } from "@/components/ui/dialog"
import { cn } from "@/lib/cn"
import { fmtCompactRelative } from "@/lib/format"
import type { TestLabProps, TestLabTab, WorkflowTracePayload } from "@/types/test_lab"

const TEST_PROGRESS_POLL_MS = 5000
const ACTIVE_PROGRESS_STATES = new Set(["queued", "pending", "running"])

export default function TestLab({ skill, versions, agent, agents, tab, scenarios, test_runs, promote, initial_source_key }: TestLabProps) {
  // flash is rendered globally by FlashBanner inside AppShell — pulling
  // it here duplicated the toast.

  // Three sub-tabs:
  //   Design — DesignerPane (Forge co-edit)
  //   Chat   — TestPane (live free-form chat with the agent)
  //   Test   — the redesigned scenario suite (formerly "Replay & Assertions")
  const initialTab: TestLabTab =
    tab === "design" ? "design" :
    tab === "chat"   ? "chat"   : "test"
  const [activeTab, setActiveTab]             = useState<TestLabTab>(initialTab)
  const [selectedVersion, setSelectedVersion] = useState<number>(skill.version)
  const [seedMessage, setSeedMessage]         = useState<string | null>(null)
  const [seedNonce, setSeedNonce]             = useState(0)
  const [chatWorkflowTrace, setChatWorkflowTrace] = useState<WorkflowTracePayload | null>(null)

  // Suite-side state: which scenario card is selected, which scenarios are
  // mid-Run-start (optimistic spinner before the next poll), and whether
  // Run-all is in flight.
  const [selectedScenarioId, setSelectedScenarioId] = useState<number | null>(scenarios[0]?.id ?? null)
  const [busyScenarioIds, setBusyScenarioIds] = useState<Set<number>>(new Set())
  const [runAllBusy, setRunAllBusy] = useState(false)

  const selectedScenario = useMemo(
    () => scenarios.find((s) => s.id === selectedScenarioId) ?? null,
    [scenarios, selectedScenarioId]
  )

  // If the selected scenario disappears (e.g., agent picker change), fall
  // back to the first one.
  useEffect(() => {
    if (selectedScenarioId && !scenarios.find((s) => s.id === selectedScenarioId)) {
      setSelectedScenarioId(scenarios[0]?.id ?? null)
    }
  }, [scenarios, selectedScenarioId])

  function selectTab(nextTab: TestLabTab) {
    setActiveTab(nextTab)
    persistTabInUrl(nextTab)
  }

  // Inbound from the Agent Testing modal: ?fix_run_id=<id> means the
  // operator clicked "Fix with Forge" inside the rehearsal modal. Replay
  // the same discuss_payload + seed flow as the in-page button, then
  // strip the param so a refresh doesn't re-fire it.
  useEffect(() => {
    if (typeof window === "undefined") return
    const params = new URLSearchParams(window.location.search)
    const raw = params.get("fix_run_id")
    if (!raw) return
    const runId = parseInt(raw, 10)
    if (!Number.isFinite(runId) || runId <= 0) return
    fixWithForge(runId)
    params.delete("fix_run_id")
    const next = params.toString()
    const url = `${window.location.pathname}${next ? `?${next}` : ""}`
    window.history.replaceState({}, "", url)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const progressPollActive = useMemo(() => {
    if (activeTab !== "test") return false
    if (runAllBusy || busyScenarioIds.size > 0) return true
    if (isActiveProgressState(promote.rehearsal?.state)) return true
    if (test_runs.some((batch) => batch.running > 0)) return true
    return scenarios.some((scenario) => isActiveProgressState(scenario.latestRunStatus))
  }, [activeTab, busyScenarioIds.size, promote.rehearsal?.state, runAllBusy, scenarios, test_runs])

  useEffect(() => {
    if (!progressPollActive) return

    let inFlight = false
    const poll = () => {
      if (inFlight) return
      inFlight = true
      router.reload({
        only: ["scenarios", "test_runs", "promote"],
        onSuccess: () => {
          setBusyScenarioIds(new Set())
          setRunAllBusy(false)
        },
        onFinish: () => {
          inFlight = false
        },
      })
    }

    const id = window.setInterval(poll, TEST_PROGRESS_POLL_MS)
    return () => window.clearInterval(id)
  }, [progressPollActive])

  // Post-refactor: the URL path holds the parent Skill id (stable across
  // versions); the version is selected via `?version=<SkillVersion id>`.
  // If the selected version is already the working one, this is a no-op
  // (no server round-trip) — we just update local UI state.
  function switchVersion(versionNumber: number) {
    const target = versions.find((v) => v.version === versionNumber)
    if (!target) return
    switchVersionById(target.id)
  }

  // Timeline + VersionsTable emit SkillVersion ids directly.
  function switchVersionById(skillVersionId: number) {
    if (skillVersionId === skill.versionId) {
      const v = versions.find((x) => x.id === skillVersionId)
      if (v) setSelectedVersion(v.version)
      return
    }
    // Preserve the active sub-tab across version switches — the controller
    // honors ?tab=<design|chat|test>. Without this the URL drops the tab
    // and the page resets to the default ("test") on every version flip.
    router.visit(`/skills/${skill.id}/test?version=${skillVersionId}&tab=${activeTab}`, { preserveScroll: false })
  }

  // "Discuss with Forge" from a live chat convo (Chat sub-tab): synthesize
  // the last few turns into a Designer composer pre-fill, then flip to
  // Design so Forge can iterate on the prompt.
  async function discussTestConvo(convoId: number, mode: "discuss" | "test_case_exact" | "test_case_liberties" = "discuss") {
    try {
      const query = new URLSearchParams({ convo_id: String(convoId) })
      if (mode !== "discuss") query.set("mode", mode)
      const res = await fetch(`/skills/${skill.id}/test/agent_chat/discuss?${query.toString()}`, {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      })
      if (!res.ok) return
      const data = await res.json() as { message?: string }
      if (!data.message) return
      setSeedMessage(data.message)
      setSeedNonce((n) => n + 1)
      selectTab("design")
    } catch {
      // Surfaced through the normal flash path on the next request.
    }
  }

  const handleChatWorkflowTrace = useCallback((trace: WorkflowTracePayload | null) => {
    setChatWorkflowTrace(trace)
  }, [])

  // "Fix with Forge" on a failed run: fetch the synthesized operator
  // message and seed the Design composer with it. Same backend path the
  // shipped "Discuss with Forge" button used. Toasts on failure so the
  // operator isn't left wondering why nothing happened.
  async function fixWithForge(runId: number) {
    try {
      const res = await fetch(`/skills/${skill.id}/test_runs/${runId}/discuss_payload`, {
        credentials: "same-origin",
        headers: { Accept: "application/json" },
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({})) as { error?: string }
        toast.error(data.error || `Couldn't load failure context (HTTP ${res.status})`)
        return
      }
      const data = await res.json() as { message?: string }
      if (!data.message) {
        toast.error("Forge has nothing to discuss — the run has no recoverable context.")
        return
      }
      setSeedMessage(data.message)
      setSeedNonce((n) => n + 1)
      selectTab("design")
    } catch {
      toast.error("Network error — try again")
    }
  }

  // Run a single scenario against the version currently selected in the
  // Test Lab. Do not replay the previous run directly: the previous run
  // may have been stamped to an older draft, while this button means
  // "run this scenario against what I am looking at now."
  async function runScenario(scenarioId: number) {
    const s = scenarios.find((x) => x.id === scenarioId)
    if (!s) return

    setBusyScenarioIds((prev) => new Set(prev).add(scenarioId))
    try {
      const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? ""
      const headers: HeadersInit = { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrf }

      const res = await fetch(`/skills/${skill.id}/test_runs`, {
        method: "POST", credentials: "same-origin", headers,
        body: JSON.stringify({ scenario_ids: [scenarioId], agent_id: agent?.id, skill_version_id: skill.versionId }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      router.reload({
        only: ["scenarios", "test_runs", "promote"],
        onSuccess: () => {
          setBusyScenarioIds(new Set())
          setRunAllBusy(false)
        },
      })
    } catch {
      setBusyScenarioIds((prev) => {
        const next = new Set(prev)
        next.delete(scenarioId)
        return next
      })
    }
  }

  async function runAll() {
    if (scenarios.length === 0) return
    setRunAllBusy(true)
    try {
      const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? ""
      const res = await fetch(`/skills/${skill.id}/test_runs`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrf },
        body: JSON.stringify({ agent_id: agent?.id, skill_version_id: skill.versionId }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      router.reload({
        only: ["scenarios", "test_runs", "promote"],
        onSuccess: () => {
          setBusyScenarioIds(new Set())
          setRunAllBusy(false)
        },
      })
    } catch {
      setRunAllBusy(false)
    }
  }

const suiteStats = useMemo(() => {
    const total = scenarios.length
    const passed = scenarios.filter((s) => s.latestRunStatus === "passed").length
    const failed = scenarios.filter((s) => s.latestRunStatus === "failed").length
    const needsReview = scenarios.filter((s) => s.latestRunStatus === "errored" || s.latestRunStatus === "skipped").length
    const lastRunAt = scenarios.reduce<string | null>((latest, s) => {
      if (!s.latestRunAt) return latest
      if (!latest) return s.latestRunAt
      return new Date(s.latestRunAt).getTime() > new Date(latest).getTime() ? s.latestRunAt : latest
    }, null)

    return {
      total,
      passed,
      failed,
      needsReview,
      passRate: total > 0 ? Math.round((passed / total) * 100) : 0,
      lastRunAt,
    }
  }, [scenarios])

  return (
    <AppShell fullBleed>
      <div className="min-h-screen bg-app-bg text-app-text">
        <LabTopBar
          skill={skill}
          versions={versions}
          agent={agent}
          selectedVersion={selectedVersion}
          stats={suiteStats}
          promote={promote}
          onRefresh={() => router.reload({ only: ["scenarios", "test_runs", "promote"] })}
          onSwitchVersion={switchVersion}
        />

        <div className="flex min-h-[calc(100vh-4rem)] flex-col gap-4 p-3 sm:p-4 lg:p-5">
          <div className="valet-panel overflow-hidden rounded-card">
            <div className="grid gap-3 border-b border-border px-4 py-3 xl:grid-cols-[max-content_minmax(0,1fr)_auto] xl:items-center">
              <TestLabTabs active={activeTab} onSelect={selectTab} />
              <div className="min-w-0 xl:justify-self-stretch">
                <VersionTimeline
                  versions={versions}
                  selectedVersionId={skill.versionId}
                  onSelect={switchVersionById}
                />
              </div>
              <div className="flex min-w-0 xl:justify-self-end">
                {skill.state === "draft" && skill.versionId != null && (
                  <button
                    type="button"
                    onClick={() => {
                      if (!confirm(`Delete draft v${skill.version}? This cannot be undone.`)) return
                      router.delete(`/skills/${skill.id}/versions/${skill.versionId}`)
                    }}
                    className="shrink-0 rounded-control border border-status-error/40 px-3 py-1.5 text-xs font-medium text-status-error transition hover:bg-status-error/10"
                    title="Delete this draft version"
                  >
                    Delete draft
                  </button>
                )}
              </div>
            </div>
          </div>

          {activeTab === "design" && (
            <section className="min-h-0 flex-1">
              <DesignerPane
                skillId={skill.id}
                skillSnapshot={skill as unknown as Record<string, unknown>}
                scenarios={scenarios as unknown as Array<Record<string, unknown>>}
                isDraft={skill.state === "draft"}
                seedMessage={seedMessage}
                seedNonce={seedNonce}
                onSeedConsumed={() => setSeedMessage(null)}
              />
            </section>
          )}

          {activeTab === "chat" && (
            <section className="grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)] gap-4 xl:grid-cols-[minmax(0,1fr)_340px]">
              <TestPane
                skillId={skill.id}
                skillKey={skill.key}
                defaultSkillVersionId={skill.versionId}
                agents={agents}
                defaultAgentId={agent?.id ?? null}
                initialSourceKey={initial_source_key}
                onDiscussConvo={discussTestConvo}
                onWorkflowTrace={handleChatWorkflowTrace}
              />
              <ActiveSkillCardsRail trace={chatWorkflowTrace} />
            </section>
          )}

          {activeTab === "test" && (
            <section className="flex min-h-0 flex-1 flex-col gap-4">
              <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[minmax(384px,520px)_minmax(0,1fr)]">
                <ScenarioList
                  scenarios={scenarios}
                  selectedId={selectedScenarioId}
                  onSelect={setSelectedScenarioId}
                  onRunOne={runScenario}
                  onRunAll={runAll}
                  busyScenarioIds={busyScenarioIds}
                  runAllBusy={runAllBusy}
                  skillId={skill.id}
                  latestBatch={test_runs[0] ?? null}
                />
                <div className="min-h-[620px] lg:min-h-0">
                  {selectedScenario ? (
                    <ScenarioDetail
                      skillId={skill.id}
                      skillVersionId={skill.versionId}
                      scenario={selectedScenario}
                      failurePaneMode="chat"
                      onFix={() => selectedScenario.latestRunId
                        ? fixWithForge(selectedScenario.latestRunId)
                        : Promise.resolve()}
                    />
                  ) : (
                    <div className="valet-panel flex h-full min-h-[360px] items-center justify-center rounded-card p-6 text-center text-sm text-app-muted">
                      {scenarios.length === 0
                        ? "No scenarios for this skill yet."
                        : "Select a scenario from the left to see the conversation and assertions."}
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}

          <div className="flex justify-end border-t border-border/70 pt-3 text-sm text-app-muted">
            <Link href={`/skills/${skill.id}/edit`} className="hover:text-app-text">
              View manual config →
            </Link>
          </div>
        </div>
      </div>
    </AppShell>
  )
}

type SuiteStats = {
  total: number
  passed: number
  failed: number
  needsReview: number
  passRate: number
  lastRunAt: string | null
}

type ActiveSkillCardRow = {
  key: string
  name: string
  status: string
  isForeground: boolean
  fieldPreviewRows: SkillCardPreviewRow[]
  filePreviewRows: SkillCardPreviewRow[]
  policyPreviewRows: SkillCardPreviewRow[]
  actionPreviewRows: SkillCardPreviewRow[]
  card: Record<string, unknown>
}

type SkillCardPreviewRow = {
  key: string
  value: string
  checked: boolean
  status: string | null
}

type SkillDebugRow = {
  key: string
  value: string
  status: string | null
  details: Array<[string, string]>
}

function ActiveSkillCardsRail({ trace }: { trace: WorkflowTracePayload | null }) {
  const cards = useMemo(() => activeSkillCardRows(trace), [trace])
  const skillCards = asRecord(trace?.skillCards)
  const foreground = stringish(asRecord(trace?.state).foreground) || stringish(skillCards.foreground)
  const [inspectingKey, setInspectingKey] = useState<string | null>(null)
  const inspectingCard = cards.find((card) => card.key === inspectingKey) ?? null

  useEffect(() => {
    if (inspectingKey && !cards.some((card) => card.key === inspectingKey)) {
      setInspectingKey(null)
    }
  }, [cards, inspectingKey])

  return (
    <>
      <aside className="valet-panel overflow-hidden rounded-card">
        <header className="border-b border-border px-4 py-3">
          <h3 className="text-sm font-semibold text-app-text">Active Skill Cards</h3>
          <p className="mt-0.5 truncate text-xs text-app-muted">
            {cards.length > 0
              ? `${cards.length} active${foreground ? " · foreground card" : ""}`
              : "Waiting for runtime state"}
          </p>
        </header>

        {cards.length === 0 ? (
          <div className="px-4 py-5 text-sm text-app-muted">
            Send a test message and the active Score Card runtime state will show here.
          </div>
        ) : (
          <div className="divide-y divide-border">
            {cards.map((card) => (
              <div
                key={card.key}
                className={cn(
                  "px-4 py-3",
                  card.isForeground ? "bg-brand-redSoft/20" : "bg-transparent",
                )}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-app-text">{card.name}</div>
                  </div>
                  <span
                    className={cn(
                      "shrink-0 rounded-chip border px-2 py-1 text-[11px] font-medium capitalize",
                      card.status === "active" && "border-brand-redBorder bg-brand-redSoft text-brand-red",
                      card.status === "waiting_on_user" && "border-vip-goldBorder bg-vip-goldMute text-vip-goldSoft",
                      card.status !== "active" && card.status !== "waiting_on_user" && "border-border bg-white/[0.04] text-app-muted",
                    )}
                  >
                    {card.status || "active"}
                  </span>
                </div>

                <div className="mt-3 space-y-2">
                  <SkillCardMiniPreview title="Fields" rows={card.fieldPreviewRows} empty="No fields yet" />
                  <SkillCardMiniPreview title="Files" rows={card.filePreviewRows} empty="No files yet" />
                  <SkillCardMiniPreview title="Policy" rows={card.policyPreviewRows} empty="No policy evaluated" />
                  <SkillCardMiniPreview title="Action" rows={card.actionPreviewRows} empty="No action run" />
                </div>

                <div className="mt-3 flex items-center justify-between gap-2 text-[10px] text-app-subtle">
                  <div className="min-w-0 truncate">{card.isForeground && "foreground"}</div>
                  <button
                    type="button"
                    onClick={() => setInspectingKey(card.key)}
                    className="inline-flex shrink-0 items-center gap-1 rounded-control border border-border bg-white/[0.04] px-2 py-1 text-[11px] font-medium text-app-muted transition hover:border-app-muted/50 hover:text-app-text"
                    title={`Inspect ${card.name} state`}
                  >
                    <Eye size={12} />
                    Inspect
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </aside>

      <SkillCardStateDialog
        card={inspectingCard}
        open={inspectingCard !== null}
        onClose={() => setInspectingKey(null)}
      />
    </>
  )
}

function SkillCardStateDialog({
  card,
  open,
  onClose,
}: {
  card: ActiveSkillCardRow | null
  open: boolean
  onClose: () => void
}) {
  if (!card) return null

  const rawCard = card.card
  const overviewRows: Array<[string, string]> = [
    ["status", stringish(rawCard.status) || "active"],
    ["foreground", card.isForeground ? "yes" : "no"],
    ["started", compactDisplayValue(rawCard.started_at)],
    ["updated", compactDisplayValue(rawCard.updated_at)],
  ]

  return (
    <Dialog open={open} onClose={onClose} size="xl">
      <DialogHeader
        title="Skill Card State"
        subtitle={
          <span className="flex min-w-0 flex-wrap items-center gap-2">
            <span>{card.name}</span>
            <span>{card.status || "active"}</span>
            {card.isForeground && <span>foreground</span>}
          </span>
        }
      />
      <DialogBody className="max-h-[76vh] overflow-y-auto">
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {overviewRows.map(([label, value]) => (
            <div key={label} className="min-w-0 rounded-control border border-border bg-app-bg px-3 py-2">
              <div className="text-[10px] uppercase text-app-subtle">{label}</div>
              <div className="mt-1 truncate font-mono text-xs text-app-text" title={value}>
                {value}
              </div>
            </div>
          ))}
        </div>

        <div className="grid gap-3 lg:grid-cols-2">
          <SkillCardStateSection title="Fields" rows={fieldDebugRows(rawCard.fields)} />
          <SkillCardStateSection title="Files" rows={fileDebugRows(rawCard.files)} />
          <SkillCardStateSection title="Data Loaders" rows={dataDebugRows(rawCard.data)} />
          <SkillCardStateSection title="Policies" rows={policyDebugRows(rawCard.policies)} />
          <SkillCardStateSection title="Actions" rows={actionDebugRows(rawCard.actions)} />
          <SkillCardStateSection title="Markers" rows={markerDebugRows(rawCard.markers)} />
        </div>
      </DialogBody>
      <DialogFooter>
        <button
          type="button"
          onClick={onClose}
          className="rounded-control bg-brand-red px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-red/90"
        >
          Close
        </button>
      </DialogFooter>
    </Dialog>
  )
}

function SkillCardMiniPreview({ title, rows, empty }: { title: string; rows: SkillCardPreviewRow[]; empty: string }) {
  return (
    <div className="rounded-control border border-border bg-white/[0.03] p-2">
      <div className="mb-1 text-[10px] uppercase tracking-wide text-app-subtle">{title}</div>
      {rows.length === 0 ? (
        <div className="text-[11px] text-app-muted">{empty}</div>
      ) : (
        <div className="space-y-1">
          {rows.map((row) => (
            <div key={row.key} className="flex min-w-0 items-center gap-2 text-[11px]">
              <span className="min-w-0 flex-1 truncate text-app-text">{row.key}</span>
              <SkillCardPreviewMark checked={row.checked} label={`${row.key} ${row.checked ? "confirmed" : "unconfirmed"}`} />
              <code className="max-w-[45%] truncate text-app-muted" title={row.value}>{row.value}</code>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function SkillCardStateSection({ title, rows }: { title: string; rows: SkillDebugRow[] }) {
  return (
    <details className="rounded-control border border-border bg-app-bg p-3" open={rows.length > 0}>
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3">
        <span className="text-sm font-medium text-app-text">{title}</span>
        <span className="rounded-chip border border-border bg-white/[0.04] px-2 py-0.5 text-[11px] text-app-muted">{rows.length}</span>
      </summary>
      {rows.length === 0 ? (
        <div className="mt-3 text-xs text-app-muted">Empty</div>
      ) : (
        <div className="mt-3 space-y-2">
          {rows.map((row) => (
            <div key={row.key} className="rounded-control border border-border bg-white/[0.03] p-2 text-xs">
              <div className="flex min-w-0 items-center justify-between gap-2">
                <div className="min-w-0 truncate font-medium text-app-text">{row.key}</div>
                {row.status && (
                  <span className="shrink-0 rounded-chip border border-border bg-white/[0.04] px-2 py-0.5 text-[11px] text-app-muted">
                    {row.status}
                  </span>
                )}
              </div>
              <div className="mt-1 min-w-0 truncate font-mono text-[11px] text-app-muted" title={row.value}>{row.value}</div>
              {row.details.length > 0 && (
                <div className="mt-2 grid gap-1 sm:grid-cols-2">
                  {row.details.map(([label, value]) => (
                    <div key={label} className="min-w-0 rounded-control bg-black/20 px-2 py-1">
                      <div className="text-[9px] uppercase text-app-subtle">{label}</div>
                      <div className="truncate text-[11px] text-app-muted" title={value}>{value}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </details>
  )
}

function SkillCardPreviewMark({ checked, label }: { checked: boolean; label: string }) {
  return (
    <span
      aria-label={label}
      className={cn(
        "grid h-4 w-4 shrink-0 place-items-center rounded border text-[10px] font-bold leading-none",
        checked
          ? "border-status-success/60 bg-status-success/15 text-status-success"
          : "border-status-warning/60 bg-status-warning/15 text-status-warning",
      )}
    >
      {checked ? "✓" : "?"}
    </span>
  )
}

function activeSkillCardRows(trace: WorkflowTracePayload | null): ActiveSkillCardRow[] {
  const skillCards = asRecord(trace?.skillCards)
  const active = asRecord(skillCards.active)
  const foreground = stringish(skillCards.foreground) || stringish(asRecord(trace?.state).foreground)
  const inactiveStatuses = new Set(["completed", "declined", "inactive"])

  return Object.entries(active)
    .filter(([, value]) => {
      const card = asRecord(value)
      const status = stringish(card.status) || "active"
      return !inactiveStatuses.has(status)
    })
    .map(([key, value]) => {
      const card = asRecord(value)
      const status = stringish(card.status) || "active"
      return {
        key,
        name: stringish(card.skill_name) || stringish(card.name) || key,
        status,
        isForeground: key === foreground,
        fieldPreviewRows: fieldPreviewRowsFor(card.fields),
        filePreviewRows: filePreviewRowsFor(card.files),
        policyPreviewRows: policyPreviewRowsFor(card.policies),
        actionPreviewRows: actionPreviewRowsFor(card.actions),
        card,
      }
    })
    .sort((a, b) => Number(b.isForeground) - Number(a.isForeground) || a.key.localeCompare(b.key))
}

function fieldPreviewRowsFor(value: unknown): SkillCardPreviewRow[] {
  return Object.entries(asRecord(value)).slice(0, 3).map(([key, raw]) => {
    const row = asRecord(raw)
    const status = stringish(row.state)
    return {
      key,
      value: fieldDisplayValue(row),
      checked: status === "confirmed",
      status,
    }
  })
}

function filePreviewRowsFor(value: unknown): SkillCardPreviewRow[] {
  return Object.entries(asRecord(value)).slice(0, 3).map(([key, raw]) => {
    const row = asRecord(raw)
    const status = stringish(row.status)
    return {
      key,
      value: stringish(row.filename) || stringish(row.content_type) || status || "file",
      checked: status === "accepted",
      status,
    }
  })
}

function policyPreviewRowsFor(value: unknown): SkillCardPreviewRow[] {
  return Object.entries(asRecord(value)).slice(0, 1).map(([key, raw]) => {
    const row = asRecord(raw)
    const status = stringish(row.verdict)
    return {
      key,
      value: status || "not evaluated",
      checked: status !== null && status !== "no_match",
      status,
    }
  })
}

function actionPreviewRowsFor(value: unknown): SkillCardPreviewRow[] {
  return Object.entries(asRecord(value)).slice(0, 1).map(([key, raw]) => {
    const row = asRecord(raw)
    const status = stringish(row.status)
    return {
      key,
      value: compactDisplayValue(sanitizeSkillDebugValue(row.result), status || "action"),
      checked: status === "succeeded" || status === "completed",
      status,
    }
  })
}

function fieldDebugRows(value: unknown): SkillDebugRow[] {
  return Object.entries(asRecord(value)).map(([key, raw]) => {
    const row = asRecord(raw)
    return {
      key,
      value: fieldDisplayValue(row),
      status: stringish(row.state),
      details: compactDebugDetails([
        ["confidence", confidenceLabel(row.confidence)],
        ["evidence", stringish(row.evidence)],
        ["validation", validationErrorsLabel(row.validation_errors)],
      ]),
    }
  })
}

function fileDebugRows(value: unknown): SkillDebugRow[] {
  return Object.entries(asRecord(value)).map(([key, raw]) => {
    const row = asRecord(raw)
    return {
      key,
      value: stringish(row.filename) || "file",
      status: stringish(row.status),
      details: compactDebugDetails([
        ["type", stringish(row.content_type)],
        ["size", bytesLabel(row.byte_size)],
        ["accepted", stringish(row.accepted_at)],
      ]),
    }
  })
}

function dataDebugRows(value: unknown): SkillDebugRow[] {
  return Object.entries(asRecord(value)).map(([key, raw]) => {
    const row = asRecord(raw)
    return {
      key,
      value: stringish(row.endpoint) || stringish(row.tool) || "loader",
      status: stringish(row.status),
      details: compactDebugDetails([
        ["loaded", stringish(row.loaded_at)],
      ]),
    }
  })
}

function policyDebugRows(value: unknown): SkillDebugRow[] {
  return Object.entries(asRecord(value)).map(([key, raw]) => {
    const row = asRecord(raw)
    return {
      key,
      value: stringish(row.verdict) || "not evaluated",
      status: stringish(row.verdict),
      details: compactDebugDetails([
        ["evaluated", stringish(row.evaluated_at)],
      ]),
    }
  })
}

function actionDebugRows(value: unknown): SkillDebugRow[] {
  return Object.entries(asRecord(value)).map(([key, raw]) => {
    const row = asRecord(raw)
    return {
      key,
      value: compactDisplayValue(sanitizeSkillDebugValue(row.result), stringish(row.status) || "action"),
      status: stringish(row.status),
      details: compactDebugDetails([
        ["ran", stringish(row.ran_at)],
        ["result", sanitizedDetail(row.result)],
      ]),
    }
  })
}

function markerDebugRows(value: unknown): SkillDebugRow[] {
  return Object.entries(asRecord(value)).map(([key, raw]) => {
    const row = asRecord(raw)
    const set = row.set === true
    return {
      key,
      value: set ? "set" : "not set",
      status: set ? "set" : null,
      details: compactDebugDetails([
        ["set at", stringish(row.set_at)],
      ]),
    }
  })
}

function fieldDisplayValue(row: Record<string, unknown>): string {
  return stringish(row.label) || compactDisplayValue(row.value, "set")
}

function compactDebugDetails(rows: Array<[string, string | null]>): Array<[string, string]> {
  return rows.filter((row): row is [string, string] => row[1] !== null && row[1] !== "")
}

function confidenceLabel(value: unknown): string | null {
  if (typeof value === "number" && Number.isFinite(value)) return `${Math.round(value * 100)}%`
  return stringish(value)
}

function validationErrorsLabel(value: unknown): string | null {
  if (Array.isArray(value)) return value.map((row) => compactDisplayValue(row)).filter((row) => row !== "—").join(", ") || null
  if (value && typeof value === "object") return compactDisplayValue(sanitizeSkillDebugValue(value))
  return stringish(value)
}

function bytesLabel(value: unknown): string | null {
  const n = typeof value === "number" ? value : Number(value)
  if (!Number.isFinite(n) || n <= 0) return null
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

function sanitizedDetail(value: unknown): string | null {
  if (value === null || value === undefined || value === "") return null
  const label = compactDisplayValue(sanitizeSkillDebugValue(value), "")
  return label === "" || label === "—" ? null : label
}

function compactDisplayValue(value: unknown, fallback = "—"): string {
  if (value === null || value === undefined || value === "") return fallback
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return truncateDebugValue(String(value), fallback)
  }

  const sanitized = sanitizeSkillDebugValue(value)
  if (typeof sanitized === "string" || typeof sanitized === "number" || typeof sanitized === "boolean") {
    return truncateDebugValue(String(sanitized), fallback)
  }

  const record = asRecord(sanitized)
  for (const key of ["label", "name", "value", "title", "status", "state", "verdict", "filename", "endpoint", "tool"]) {
    const candidate = stringish(record[key])
    if (candidate) return truncateDebugValue(candidate, fallback)
  }

  try {
    return truncateDebugValue(JSON.stringify(sanitized), fallback)
  } catch {
    return fallback
  }
}

function truncateDebugValue(value: string, fallback = "—"): string {
  const trimmed = value.trim()
  if (!trimmed) return fallback
  return trimmed.length > 140 ? `${trimmed.slice(0, 137)}...` : trimmed
}

function sanitizeSkillDebugValue(value: unknown, depth = 0): unknown {
  if (depth > 3) return "[truncated]"
  if (Array.isArray(value)) return value.slice(0, 6).map((row) => sanitizeSkillDebugValue(row, depth + 1))
  if (!value || typeof value !== "object") return value

  const out: Record<string, unknown> = {}
  Object.entries(value as Record<string, unknown>).forEach(([key, row]) => {
    if (hiddenSkillDebugKey(key)) return
    out[key] = sanitizeSkillDebugValue(row, depth + 1)
  })
  return Object.keys(out).length > 0 ? out : "present"
}

function hiddenSkillDebugKey(key: string): boolean {
  const normalized = key.toLowerCase()
  return normalized === "id" ||
    normalized === "uuid" ||
    normalized.endsWith("_id") ||
    normalized.endsWith("_ids") ||
    normalized.endsWith("_uuid") ||
    normalized === "input_signature" ||
    normalized === "valet_meta" ||
    normalized === "signed_id" ||
    normalized === "blob_id" ||
    normalized === "accepted_reason"
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {}
}

function stringish(value: unknown): string | null {
  if (typeof value === "string" && value.trim()) return value
  if (typeof value === "number" || typeof value === "boolean") return String(value)
  return null
}

function LabTopBar({
  skill,
  versions,
  agent,
  selectedVersion,
  stats,
  promote,
  onRefresh,
  onSwitchVersion,
}: {
  skill: TestLabProps["skill"]
  versions: TestLabProps["versions"]
  agent: TestLabProps["agent"]
  selectedVersion: number
  stats: SuiteStats
  promote: TestLabProps["promote"]
  onRefresh: () => void
  onSwitchVersion: (versionNumber: number) => void
}) {
  return (
    <header className="border-b border-border bg-app-panel/85 px-4 py-3 backdrop-blur xl:px-5">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex min-w-0 flex-wrap items-center gap-3 xl:gap-4">
          <Link href="/skills" className="flex items-center gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-brand-red text-lg font-bold text-white shadow-redGlow">
              V
            </span>
            <span className="text-xl font-semibold tracking-tight text-white">
              Valet<span className="text-brand-red">.red</span> lab
            </span>
          </Link>

          <div className="hidden h-8 w-px bg-white/10 md:block" />

          <div className="min-w-0">
            <div className="flex items-center gap-2 text-base font-semibold text-white">
              <FlaskConical size={16} className="text-brand-red" />
              <span>Skill test lab</span>
            </div>
            <div className="mt-0.5 truncate text-xs text-app-muted">
              {agent ? `${agent.name} · ${agent.key}` : `${skill.agentAssignments.length} attached agent${skill.agentAssignments.length === 1 ? "" : "s"}`}
            </div>
          </div>

          <Link
            href={`/skills/${skill.id}/edit`}
            className="inline-flex min-h-10 max-w-[260px] items-center gap-2 rounded-control border border-border bg-white/[0.04] px-3 text-sm text-white transition hover:bg-white/[0.07]"
            title="Open the manual prompt + tools editor"
          >
            <span className="truncate">{skill.key}</span>
            <ChevronDown size={14} className="shrink-0 text-app-muted" />
          </Link>

          {skill.isGlobal && <StateChip label="Global" tone="neutral" />}
        </div>

        <div className="flex flex-wrap items-center gap-2 xl:justify-end">
          <div className="mr-1 hidden text-right text-xs text-app-muted sm:block">
            <div>Last run</div>
            <div className="text-white">{fmtCompactRelative(stats.lastRunAt)}</div>
          </div>

          <button
            type="button"
            onClick={onRefresh}
            className="grid h-10 w-10 place-items-center rounded-control border border-border bg-white/[0.04] text-app-muted transition hover:bg-white/[0.07] hover:text-white"
            aria-label="Refresh test results"
            title="Refresh test results"
          >
            <RefreshCw size={15} />
          </button>

          <div className="hidden items-center gap-2 2xl:flex">
            <MetricTile label="Scenarios" value={String(stats.total)} />
            <MetricTile label="Pass rate" value={`${stats.passRate}%`} tone="success" />
            <MetricTile label="Failed" value={String(stats.failed)} tone="danger" />
            <MetricTile label="Errored/skipped" value={String(stats.needsReview)} tone="warning" />
          </div>

          <ActivityFeedButton entries={skill.editHistory ?? []} title="Skill activity" />

          <VersionSelector
            skillId={skill.id}
            selectedVersion={selectedVersion}
            versions={versions}
            isGlobal={skill.isGlobal}
            onSwitchVersion={onSwitchVersion}
            canPromote={promote.canPromote}
            blockerCount={promote.blockerCount}
            rehearsal={promote.rehearsal}
          />

          <Link
            href="/docs"
            className="inline-flex min-h-10 items-center gap-2 rounded-control border border-border bg-white/[0.04] px-3 text-sm text-white transition hover:bg-white/[0.07]"
          >
            <BookOpen size={15} />
            Docs
          </Link>

        </div>
      </div>
    </header>
  )
}

function MetricTile({ label, value, tone = "neutral" }: { label: string; value: string; tone?: "neutral" | "success" | "danger" | "warning" }) {
  return (
    <div className="min-w-[86px] rounded-control border border-border bg-white/[0.04] px-3 py-2">
      <div className="text-[11px] text-app-muted">{label}</div>
      <div
        className={cn(
          "text-base font-semibold",
          tone === "success" && "text-status-success",
          tone === "danger" && "text-status-error",
          tone === "warning" && "text-status-warning",
          tone === "neutral" && "text-white",
        )}
      >
        {value}
      </div>
    </div>
  )
}

function isActiveProgressState(status: string | null | undefined): boolean {
  return ACTIVE_PROGRESS_STATES.has(status ?? "")
}

function persistTabInUrl(tab: TestLabTab) {
  if (typeof window === "undefined") return

  const params = new URLSearchParams(window.location.search)
  params.set("tab", tab)
  const nextQuery = params.toString()
  const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ""}${window.location.hash}`
  window.history.replaceState(window.history.state, "", nextUrl)
}

function StateChip({ label, tone }: { label: string; tone: "gold" | "red" | "neutral" }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-chip border px-3 py-1 text-xs font-semibold capitalize",
        tone === "gold" && "border-vip-goldBorder bg-vip-goldMute text-vip-goldSoft",
        tone === "red" && "border-brand-redBorder bg-brand-redSoft text-brand-red",
        tone === "neutral" && "border-border bg-white/[0.04] text-app-muted",
      )}
    >
      {label}
    </span>
  )
}
